import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchFeedbackConfig, FeedbackConfig, submitFeedback, SubmitFeedbackPayload } from "@/features/Feedback/api/feedbackApi";
import { analyticsService } from "@/shared/services/analytics";

interface UseFeedbackPromptOptions {
  isAuthenticated: boolean;
  userId?: string;
}

export interface FeedbackPromptState {
  isLoading: boolean;
  isReady: boolean;
  isOpen: boolean;
  config: FeedbackConfig | null;
  open: () => void;
  close: (reason?: "submitted" | "dismissed") => void;
  submit: (answers: Record<string, unknown>) => Promise<boolean>;
}

const LOCAL_STORAGE_KEY = "prodgy_user_feedback_submitted";
const SESSION_START_KEY = "prodgy_feedback_session_started_at";

/**
 * Deeplink trigger: the "Send Feedback" email CTA points at
 * `${app_url}/?feedback=1`. When a logged-in user lands with this param we
 * open the form immediately — bypassing the timeout/session-count triggers
 * AND the already-submitted gate (an email CTA must always work, even for
 * users who already responded; submitting again is desirable here). The
 * param is stripped from the URL so a refresh/back-nav doesn't re-open it.
 */
function consumeFeedbackDeeplink(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get("feedback") !== "1") return false;
  params.delete("feedback");
  const qs = params.toString();
  window.history.replaceState(
    {},
    "",
    window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash
  );
  return true;
}

export function useFeedbackPrompt(options: UseFeedbackPromptOptions): FeedbackPromptState {
  const { isAuthenticated, userId } = options;
  const { i18n } = useTranslation();
  const [config, setConfig] = useState<FeedbackConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const clearTimer = () => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const schedulePrompt = useCallback((cfg: FeedbackConfig) => {
    clearTimer();

    if (!isAuthenticated) {
      return;
    }

    if (!cfg.enabled) {
      // Feature toggled off globally — nothing to schedule.
      return;
    }

    const alreadySubmittedFlag = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (alreadySubmittedFlag === "1" || cfg.already_submitted) {
      return;
    }

    const openNow = () => {
      setIsOpen(true);
      if (cfg.ga4_enabled) {
        analyticsService.trackFeedbackImpression({
          event_name: cfg.ga4_event_name || "user_feedback",
        });
      }
    };

    // Trigger 1 — session-count: shows immediately on load when the user
    // has already reached the configured login threshold. Cheap, no timer.
    const threshold = cfg.session_count_threshold ?? 0;
    if (threshold > 0 && (cfg.login_count ?? 0) >= threshold) {
      openNow();
      return;
    }

    // Trigger 2 — session-timeout: schedule (or fire immediately if the
    // session has been open long enough already). Combined via OR — this
    // path runs only when the count trigger didn't already fire.
    const minutes = cfg.session_timeout_minutes ?? 0;
    if (!minutes || minutes <= 0) {
      return;
    }

    const totalDelayMs = minutes * 60 * 1000;
    const now = Date.now();

    const storedStart = window.localStorage.getItem(SESSION_START_KEY);
    let sessionStart = storedStart ? Number.parseInt(storedStart, 10) : NaN;

    if (!Number.isFinite(sessionStart) || sessionStart <= 0) {
      sessionStart = now;
      window.localStorage.setItem(SESSION_START_KEY, String(sessionStart));
    }

    const elapsed = now - sessionStart;
    const remaining = totalDelayMs - elapsed;

    if (remaining <= 0) {
      openNow();
      return;
    }

    timeoutRef.current = window.setTimeout(openNow, remaining);
  }, [isAuthenticated]);

  const loadConfig = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    setIsLoading(true);
    try {
      const cfg = await fetchFeedbackConfig();
      setConfig(cfg);
      setIsReady(!!cfg);

      if (cfg) {
        // Sincronizar flag local com status vindo da API para suportar reset global
        if (cfg.already_submitted) {
          window.localStorage.setItem(LOCAL_STORAGE_KEY, "1");
        } else {
          window.localStorage.removeItem(LOCAL_STORAGE_KEY);
        }

        // Deeplink from the "Send Feedback" email CTA — open right away,
        // ignoring the auto-triggers and the already-submitted gate. Only
        // honoured when the feature is enabled (cfg is null otherwise).
        if (cfg.enabled && consumeFeedbackDeeplink()) {
          setIsOpen(true);
          if (cfg.ga4_enabled) {
            analyticsService.trackFeedbackImpression({
              event_name: cfg.ga4_event_name || "user_feedback",
            });
          }
        } else {
          // Agendar exibição do popup após o tempo configurado de sessão
          schedulePrompt(cfg);
        }
      } else {
        // Se não houver configuração, limpar flag local para evitar inconsistências
        window.localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, schedulePrompt]);

  const open = useCallback(() => {
    if (!config) {
      return;
    }
    setIsOpen(true);
  }, [config]);

  const close = useCallback((reason?: "submitted" | "dismissed") => {
    setIsOpen(false);

    if (config?.ga4_enabled && reason === "dismissed") {
      analyticsService.trackFeedbackDismissed({
        event_name: config.ga4_event_name || "user_feedback",
      });
    }
  }, [config]);

  const submit = useCallback(async (answers: Record<string, unknown>): Promise<boolean> => {
    if (!config) {
      return false;
    }

    const payload: SubmitFeedbackPayload = {
      answers,
      metadata: {
        language: i18n.language,
        userId,
      },
    };

    const response = await submitFeedback(payload);

    if (response?.success) {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, "1");
      setIsOpen(false);

      if (config.ga4_enabled) {
        analyticsService.trackFeedbackSubmitted({
          event_name: config.ga4_event_name || "user_feedback",
          user_id: userId,
          timestamp: new Date().toISOString(),
          rating: answers.rating as number | undefined,
          comment: answers.comment as string | undefined,
          language: i18n.language,
        });
      }

      return true;
    }

    return false;
  }, [config, i18n.language, userId]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearTimer();
      setIsOpen(false);
      return;
    }

    loadConfig();

    return () => {
      clearTimer();
    };
  }, [isAuthenticated, loadConfig]);

  return {
    isLoading,
    isReady,
    isOpen,
    config,
    open,
    close,
    submit,
  };
}
